import PdfView from "@app/components/pdf/pdfView";
import React from "react";
import { useLocation, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { LoadingContext } from "@natera/platform/lib/components/context";

import { routes } from "@app/routing";
import {
  TestResultContext,
  ServiceContext,
  TestDetailsContext,
} from "@app/provider";
import { BlueHeaderContent } from "@app/components/ui/layout";
import { usePdfFile } from "@app/hooks";
import { getPdfViewHeapHandlers } from "@app/utils";

const messages = defineMessages({
  supplementalPdfMoreAboutResult: {
    id: "supplementalPdfMoreAboutResult",
    defaultMessage: "More about your result",
  },
  testResultCloseResults: {
    id: "testResultCloseResults",
    defaultMessage: "Close",
  },
});

type Params = {
  orderUidPublic: string;
  testUid: string;
  uid: string;
};

const SupplementalPdf: React.FC = () => {
  const intl = useIntl();
  const location = useLocation();

  const { orderUidPublic, testUid, uid } = useParams<keyof Params>() as Params;

  const [url, setUrl] = React.useState<string>("");

  const { file, pdfTokenError } = usePdfFile({ url });
  const { getSupplementalMaterials, getSupplementMaterialIsLoading } =
    React.useContext(TestResultContext);
  const { fileService } = React.useContext(ServiceContext);
  const { getTestDetails } = React.useContext(TestDetailsContext);
  const testDetails = getTestDetails();
  const { onPdfViewErrorHeap, onPdfViewSuccessHeap } = getPdfViewHeapHandlers(
    testDetails,
    orderUidPublic,
    uid
  );

  const getUrl = async () => {
    const material = await getSupplementalMaterials(
      orderUidPublic,
      testUid,
      uid
    );

    if (material && material.url) {
      setUrl(material.url);
    }
  };

  React.useEffect(() => {
    getUrl();
  }, []);

  const onDownloadPdf = async () => {
    const fileName = intl.formatMessage(
      messages.supplementalPdfMoreAboutResult
    );
    const result = await fileService.getFile(
      `/result/supplemental/${orderUidPublic}/${testUid}/${uid}`
    );
    fileService.download(result, fileName);
  };

  return (
    <div className="test-result">
      <BlueHeaderContent
        blueHeaderTitle={
          location.state ||
          intl.formatMessage(messages.supplementalPdfMoreAboutResult)
        }
        crossHeaderRoute={routes.testResultDetailsPage(orderUidPublic, testUid)}
        fullScreen
      >
        <LoadingContext isLoading={getSupplementMaterialIsLoading}>
          <PdfView
            file={file}
            onDownload={onDownloadPdf}
            onPdfViewError={onPdfViewErrorHeap}
            onPdfViewSuccess={onPdfViewSuccessHeap}
            hasError={pdfTokenError}
          />
        </LoadingContext>
      </BlueHeaderContent>
    </div>
  );
};

export default SupplementalPdf;
